import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) return setError("Passwords do not match.");
    if (!token) return setError("Missing reset token.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch (err) {
      setError(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-cream p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-border p-10">
        <div className="mb-8"><Logo /></div>
        <div className="w-12 h-12 rounded-xl bg-brand-orange/10 flex items-center justify-center mb-4">
          <KeyRound className="w-5 h-5 brand-orange" />
        </div>
        <h2 className="font-heading font-bold text-2xl">Set a new password</h2>
        <p className="text-foreground/60 text-sm mt-2">Enter your new password below.</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="pw">New password</Label>
            <Input id="pw" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" data-testid="reset-password-input" />
          </div>
          <div>
            <Label htmlFor="cpw">Confirm password</Label>
            <Input id="cpw" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" data-testid="reset-confirm-input" />
          </div>
          {error && <div className="text-sm text-destructive" data-testid="reset-error">{error}</div>}
          <Button type="submit" disabled={loading} className="w-full bg-brand-orange hover:bg-[#E65C00] text-white h-11 rounded-full font-semibold" data-testid="reset-submit-btn">
            {loading ? "Updating…" : "Update password"}
          </Button>
        </form>
        <div className="mt-6 text-sm text-foreground/60">
          <Link to="/login" className="brand-orange font-semibold hover:underline">Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
